import React, { useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Input } from "antd5";

import BidNoBidPromptModal from "components/notices/BidNoBidPromptModal";
import Button from "components/ui/button/Button";
import { useDebouncedValue } from "../../../../lib/debounce";
import {
  SearchOpportunitiesRequest,
  SearchOpportunitiesRequest_OrderByEnum_1,
  SearchOpportunitiesRequest_OrderEnum_1,
} from "../../../../lib/generated/app-service-gql/graphql";
import { useEntityOpportunities } from "../../../../lib/hooks/api/opportunities/useEntityOpportunities";
import { useInfiniteOpportunities } from "../../../../lib/hooks/api/opportunities/useOpportunities";
import UIcon from "../../../../lib/icons/untitled_ui/UIcon";
import { Text } from "../../../../styles/utility-components";
import { CreateOpportunityForm } from "../../create_opportunity/CreateOpportunityForm";
import { useSaveToOpportunity } from "../hooks/useSaveToOpportunity";
import { SharedSaveProps } from "../types";
import { SaveToOpportunityList } from "./SaveToOpportunityList";

export const LIST_CONTAINER_ID = "save-to-opportunity-list";
export const MAX_MATCHING_BUYERS = 30;

type SaveToOpportunityProps = SharedSaveProps & {
  creation: boolean;
  setCreation: (state: boolean) => void;
  onSave?: () => void;
  savedCount?: number;
};

export function SaveToOpportunity({
  defaultOpportunityValues,
  entityIds,
  entityType,
  entityData,
  creation,
  setCreation,
  onSave,
  savedCount,
}: SaveToOpportunityProps) {
  const buyerIds = defaultOpportunityValues?.entities?.flatMap((entity) => entity.entityId);

  const [filters, setFilters] = useState<SearchOpportunitiesRequest>({
    orderBy: SearchOpportunitiesRequest_OrderByEnum_1.BuyerMatching,
    order: SearchOpportunitiesRequest_OrderEnum_1.Desc,
    orderBuyerMatchingIds: buyerIds,
  });
  const [debouncedFilters] = useDebouncedValue(filters, 500);
  const { data, hasNextPage, fetchNextPage, isLoading, isError } = useInfiniteOpportunities(
    debouncedFilters,
    MAX_MATCHING_BUYERS,
  );

  const { data: savedOpportunityIds, isLoading: isLoadingSavedOpportunities } =
    useEntityOpportunities(
      // Only fetch saved opportunities for the first entity
      // TODO: Enhance to support multiple entities as the current UI
      // only works with a single entity
      entityIds[0],
    );

  const allOpportunities = useMemo(() => {
    const opportunities =
      data?.pages
        .flatMap((page) => page.opportunities.results)
        .map((opp) => ({
          ...opp,
          isSaved: savedOpportunityIds.includes(opp.id),
        })) ?? [];

    return {
      opportunities,
      total: data?.pages[0]?.opportunities.total ?? 0,
    };
  }, [data?.pages, savedOpportunityIds]);

  // Show search bar if there are initial opportunities or if user is searching
  const hasInitialOpportunities =
    !filters.searchText && (data?.pages?.[0]?.opportunities?.results ?? []).length > 0;
  const showSearchBar = hasInitialOpportunities || !!filters.searchText;

  const [newOpportunityId, setNewOpportunityId] = useState<string | undefined>();
  const [isBidNoBidPromptModalOpen, setIsBidNoBidPromptModalOpen] = useState(false);

  const { handleCreateOpportunityFromDefaultValues } = useSaveToOpportunity({
    entityIds,
    entityType,
    defaultOpportunityValues,
    savedCount,
    onShowDrawer: () => setCreation(true),
    onOpportunitySave: (optId: string) => {
      if (entityType === "notice" && entityData?.isOpenTender) {
        setNewOpportunityId(optId);
        setIsBidNoBidPromptModalOpen(true);
      }
    },
  });

  const handleCreateOpportunity = () => {
    // If the entityType is notice, we need to create a new opportunity from the default values
    // Bypassing the form entirely
    if (entityType === "notice") {
      void handleCreateOpportunityFromDefaultValues();
      onSave?.();
    } else {
      setCreation(true);
    }
  };

  if (creation) {
    return (
      <Container>
        <Text h2>Create new opportunity</Text>
        <CreateOpportunityForm
          defaultValues={defaultOpportunityValues}
          onComplete={() => {
            setCreation(false);
            onSave?.();
          }}
        />
      </Container>
    );
  }

  return (
    <>
      <Container>
        {showSearchBar && (
          <Input
            placeholder="Search"
            prefix={<UIcon icon="searchRefraction" size={16} />}
            value={filters.searchText ?? ""}
            onChange={(e) => {
              setFilters({ ...filters, searchText: e.target.value });
            }}
            allowClear
          />
        )}
        <ListContainer id={LIST_CONTAINER_ID}>
          <SaveToOpportunityList
            isLoading={isLoading || isLoadingSavedOpportunities}
            isError={isError}
            opportunities={allOpportunities}
            buyerIds={buyerIds}
            entityIds={entityIds}
            entityType={entityType}
            hasNextPage={hasNextPage || false}
            fetchNextPage={fetchNextPage}
            onSave={onSave}
          />
        </ListContainer>
        <Button buttonType="primary" onClick={handleCreateOpportunity}>
          Create new opportunity
        </Button>
      </Container>
      <BidNoBidPromptModal
        isOpen={isBidNoBidPromptModalOpen}
        onOpenChange={(open) => setIsBidNoBidPromptModalOpen(open)}
        newOpportunityId={newOpportunityId}
      />
    </>
  );
}

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  height: "100%",
});

const ListContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: 8,
  height: "100%",
  overflowY: "auto",
});
